import React from "react";
import { useLocation } from "react-router-dom";
import { refreshUsageAllowed, sendUsageEvent, USAGE_EVENTS } from "@/features/usage/usageBeacon";
import { resolveUsageRoute } from "@/features/usage/usageRoutes";

/**
 * USAGE-ANALYTICS-1 / C-322 — the one mount that makes the beacon actually beacon.
 *
 * ⚑ IT EXISTS BECAUSE I SHIPPED THE CALLER WITH NO CALL SITES. Every node passed, the vocabulary
 * matched the backend's, the body carried exactly four keys — and nothing ever called it, so the
 * row recorded nothing. That is the same defect I had reported in someone else's work hours
 * earlier: a correct module proves nothing about whether anybody reaches it.
 *
 * ⚑ THE PREFERENCE IS READ ONCE PER SESSION, NOT ONCE PER SETTINGS VISIT. Before this, the only
 * thing that called `setUsageAllowed` was the Settings panel, so for a teacher who never opened
 * Settings → Privacy the answer stayed UNKNOWN and the beacon was silent for ever. Unknown still
 * means silence — a failed read must never become consent — but now the question gets asked.
 *
 * ⚑ `page_view` CARRIES A DECLARED TEMPLATE OR NO ROUTE AT ALL. The server does exact set
 * membership and never trusts the string, so a real path with an id in it would bucket as
 * `(unresolved)` for a page we do know. `resolveUsageRoute` maps the location to one of the 77
 * generated templates, and when nothing matches the event goes without a `route` key rather than
 * with a wrong one.
 *
 * ⚑ `visible_ms` IS TIME THE PAGE WAS ACTUALLY VISIBLE, not time since navigation. A tab left open
 * in the background is not engagement — the service's own ACTIVE_MIN_VISIBLE_MS exists to say so —
 * so the clock stops on `visibilitychange` and the beacon fires on leaving the route.
 *
 * Renders nothing. Mounted inside the router, above the routes.
 */
export function UsageBeaconMount() {
  const location = useLocation();
  const pathname = location.pathname;

  // One question per session. React's StrictMode double-invokes effects in development, so the
  // ref — not the empty dependency array — is what makes "once" true.
  const asked = React.useRef(false);
  React.useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    refreshUsageAllowed();
  }, []);

  React.useEffect(() => {
    const template = resolveUsageRoute(pathname);
    let visibleMs = 0;
    let since = typeof document !== "undefined" && document.visibilityState === "hidden"
      ? null
      : Date.now();

    const accumulate = () => {
      if (since !== null) {
        visibleMs += Date.now() - since;
        since = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") accumulate();
      else if (since === null) since = Date.now();
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      accumulate();
      // The beacon decides whether it may send; this only decides what to say. An unresolved
      // path contributes no `route` key at all.
      sendUsageEvent(USAGE_EVENTS.PAGE_VIEW, {
        route: template || undefined,
        visibleMs,
      });
    };
  }, [pathname]);

  return null;
}

export default UsageBeaconMount;
